/**
 * Companion idle "next-best-thing" suggestion types.
 *
 * Per plan §3: when a workspace tab is in *Idle-companion* state (no active
 * query routed by Ando), the top of the tab shows a concrete, proactive
 * 1-liner like:
 *   "3 email non lette da clienti chiave · 1 richiede risposta entro oggi"
 *      → *Triage con Ando*
 *
 * The render is owned by `@ando/ui/CompanionHero` (presentation-only). The
 * data layer here is consumed by `apps/client/features/workspace/hooks/
 * useCompanionSuggestion.ts` which returns the suggestion for the active
 * tab. Today the data source is a mock fixture; F4+ swaps it for a BE-fed
 * `/api/v1/companion/suggestions` endpoint with zero call-site changes.
 *
 * Hard rules (per plan §3):
 *   - Subject + concrete number/time + action verb. NEVER stats-only.
 *   - One suggestion per tab at a time (the "next" best thing, not a list).
 *   - Priority drives sort order when multiple competing suggestions exist.
 */

import { z } from "zod";
import { WorkspaceTabSchema, type WorkspaceTab } from "../domain/workspace.js";

/** Priority bucket — drives sort + visual emphasis. */
export const CompanionPrioritySchema = z.enum(["urgent", "actionable", "informational"]);
export type CompanionPriority = z.infer<typeof CompanionPrioritySchema>;

export const CompanionSuggestionSchema = z.object({
  /** Stable id — used as React key + audit. */
  id: z.string().min(1),
  /** Workspace tab the suggestion is anchored to. */
  tab: WorkspaceTabSchema,
  /** Body text (markdown allowed — `<strong>` highlights). */
  text: z.string().min(1),
  /** Short CTA copy (e.g. `"Triage con Ando"`, `"Apri brief"`). */
  ctaLabel: z.string().min(1),
  /**
   * Action the CTA triggers — typically a chat-query the workspace router
   * re-runs through `routeFromQuery` (round-trip). Keeping this as a plain
   * string keeps the type cross-target (no React/router imports here).
   */
  ctaAction: z.string().min(1),
  /** Priority bucket. */
  priority: CompanionPrioritySchema,
});

export type CompanionSuggestion = z.infer<typeof CompanionSuggestionSchema>;

export type { WorkspaceTab };
